import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
// Redux
import { useDispatch, useSelector } from 'react-redux';
// External Component
import moment from 'moment';
import { gql } from '@apollo/client';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

// Style
import withStyles from 'isomorphic-style-loader/withStyles';
import { Button, Collapse, Modal, FormGroup, FormControl, InputGroup } from 'react-bootstrap';
import cx from 'classnames';

// Component
import Avatar from '../../Avatar';
import ContactHost from '../ContactHost';
import Link from '../../Link';
import CountryList from '../../CountryList';
import Loader from '../../Loader';

// Redux Action
import { contactHostOpen } from '../../../actions/message/contactHostModal';
import { loadAccount } from '../../../actions/account';

// Helper
import showToaster from '../../../helpers/showToaster';

// Locale
import messages from '../../../locale/messages';
import { CommonAngleDownIcon, CommonAngleUpIcon } from '../../Common/Assets/SvgComponent/commonSvgIcon';
import s from './HostDetail.css';
import bt from '../../../components/commonStyle.css';

const ADD_PHONE_NUMBER = gql`
	mutation AddPhoneNumber($countryCode: String!, $phoneNumber: String!, $countryName: String, $userId: String) {
		AddPhoneNumber(countryCode: $countryCode, phoneNumber: $phoneNumber, countryName: $countryName, userId: $userId) {
			status
			phoneNumber
			countryCode
		}
	}
`;

const HostDetail = (props) => {

  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [countryCode, setCountryCode] = useState('TN');
  const [dialCode, setDialCode] = useState('+216');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState(null);
  
  const dispatch = useDispatch();
  const account = useSelector(state => state.account.data);
  
  const handleClick = () => { setOpen(!open) };

  const handleContactHost = () => {
    if (!account?.phoneNumber) {
      // No phone number - open modal with phone form
      setShowModal(true);
    } else {
      // Has phone number - proceed with contact host
      dispatch(contactHostOpen(id, urlParameters));
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const validatePhoneNumber = (phoneNumber) => {
    try {
      // Clean the number (remove spaces, dashes, etc.)
      const cleanNumber = phoneNumber.replace(/[\s\-\(\)\.]/g, '');

      // Basic checks first
      if (!cleanNumber) {
        setPhoneError('required');
        return false;
      }

      if (!/^\d+$/.test(cleanNumber)) {
        setPhoneError('digitsOnly');
        return false;
      }

      // Use libphonenumber-js for validation
      const isValid = isValidPhoneNumber(cleanNumber, countryCode);

      if (!isValid) {
        setPhoneError('invalidNumber');
        return false;
      }

      setPhoneError(null);
      return true;

    } catch (error) {
      setPhoneError('invalidNumber');
      return false;
    }
  };

  const handleCountryChange = (e, selectedData) => {
    setDialCode(selectedData.dialCode);
    setCountryCode(selectedData.countryCode);
    setPhoneError(null);

    if (phoneNumber) {
      setTimeout(() => {
        validatePhoneNumber(phoneNumber);
      }, 0);
    }
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value;
    const name = event.target.name;

    if (name === 'phoneNumber') {
      setPhoneNumber(value);
      setPhoneError(null); // Clear error on change

      // Real-time validation
      if (value) {
        validatePhoneNumber(value);
      }
    } else if (name === 'countryCode') {
      setCountryCode(value);
      setPhoneError(null);
    }
  };

  const savePhoneNumber = async () => {
    const { formatMessage } = props.intl;
    
    const isValid = validatePhoneNumber(phoneNumber);
    if (!isValid) {
      return;
    }

    setSubmitting(true);

    try {
      const addPhoneNumberAction = (countryCode, phoneNumber, countryName, userId) => {
        return async (dispatch, getState, { client }) => {
          try {
            const { data } = await client.mutate({
              mutation: ADD_PHONE_NUMBER,
              variables: {
                countryCode,
                phoneNumber,
                countryName,
                userId
              }
            });
            return data;
          } catch (error) {
            throw error;
          }
        };
      };

      const result = await dispatch(addPhoneNumberAction(dialCode, phoneNumber, countryCode, account?.userId));
      
      if (result?.AddPhoneNumber?.status === '200') {
        showToaster({ messageId: 'phoneAddedSuccess', toasterType: 'success' });
        await dispatch(loadAccount());
        setShowModal(false);
        // Now proceed with contact host
        dispatch(contactHostOpen(id, urlParameters));
      } else {
        showToaster({ messageId: 'somethingWentWrong', toasterType: 'error' });
      }
    } catch (error) {
      console.error('Error saving phone number:', error);
      showToaster({ messageId: 'somethingWentWrong', toasterType: 'error' });
    }

    setSubmitting(false);
  };

  const getErrorMessage = (errorType) => {
    const { formatMessage } = props.intl;

    const errorMessages = {
      required: formatMessage(messages.phoneRequired),
      digitsOnly: formatMessage(messages.phoneDigitsOnly),
      invalidNumber: formatMessage(messages.phoneInvalidNumber),
    };

    return errorMessages[errorType] || formatMessage(messages.phoneInvalidNumber);
  };

  const { isHost, hostEmail, userBanStatus, urlParameters } = props;
  const { id, personCapacity, userId, city, blockedDates, country } = props;
  const { profile: { profileId, firstName, picture, info, createdAt, isMigrated } } = props;
  const { listingData: { minNight, maxNight, maxDaysNotice } } = props;
  let count = 150, firstArray, restArray, dotString = false, joinedDate, initialValues;

  joinedDate = createdAt != null ? moment(parseInt(createdAt)).format("MMMM YYYY") : '';
  initialValues = {
    listId: id,
    host: userId,
    hostEmail,
    firstName,
    startDate: urlParameters.startDate,
    endDate: urlParameters.endDate,
    personCapacity: urlParameters.guests,
  };
  if (info) {
    firstArray = info.slice(0, count);
    restArray = info.slice(count, info.length);
  }
  if (restArray && restArray.length > 0) {
    dotString = true;
  }

  return (
    <div>
      <ContactHost
        initialValues={initialValues}
        id={id}
        userId={userId}
        city={city}
        profileId={profileId}
        picture={picture}
        displayName={firstName}
        personCapacity={personCapacity}
        blockedDates={blockedDates}
        minNight={minNight}
        maxNight={maxNight}
        maxDaysNotice={maxDaysNotice}
        country={country}
        isMigrated={isMigrated}
      />
      <div className={cx(s.pageContent)}>
        <div className={s.flex}>
          <div>
            <Avatar
              source={picture}
              height={63}
              width={63}
              title={firstName}
              className={s.profileAvatar}
              withLink
              linkClassName={s.profileAvatarLink}
              profileId={profileId}
              isFromMigration={isMigrated}
            />
          </div>
          <div>
            <Link to={"/users/show/" + profileId}>
              <h1 className={s.titleText}>
                <FormattedMessage {...messages.hostedBy} /> {' '}  {firstName}
              </h1>
            </Link>
            <p className={cx(s.textMuted)}><FormattedMessage {...messages.joinedIn} /> {joinedDate}</p>
          </div>
        </div>
        <div className={cx(s.infoContent)}>
          {!open && count >= 150 && dotString === true &&
            <span className={cx(s.subText, s.lineBreak)}>  {firstArray} ...</span>
          }
          {!open && count >= 150 && dotString === false &&
            <span className={cx(s.subText, s.lineBreak)}>  {firstArray}</span>
          }
          {
            restArray && restArray.length > 0 &&
            <span>
              <Collapse in={open}>
                <div> <span className={cx(s.subText, s.lineBreak)}>
                  {firstArray}{restArray}
                </span></div>
              </Collapse>
              {
                dotString && <div className={s.btnContainer}>
                  <div className={s.showHidePadding}>
                    <Button
                      bsStyle="link"
                      className={cx(s.button, s.noPadding, s.btnLInk, s.showHideBtn, 'bgTransparent')}
                      onClick={() => handleClick()}
                    >
                      {
                        !open ? <FormattedMessage {...messages.showMore} /> : <FormattedMessage {...messages.showLess} />
                      }
                      {
                        open ? <CommonAngleUpIcon className1={s.navigationIcon} /> : <CommonAngleDownIcon className2={s.navigationIcon} />
                      }
                    </Button>
                  </div>
                </div>
              }
            </span>
          }

        </div>
        {
          !isHost && !userBanStatus && <Button className={cx(s.btn, bt.btnLarge, bt.btnPrimary)} onClick={handleContactHost}>
            <FormattedMessage {...messages.contactHost} />
          </Button>
        }
      </div>
      <div className={s.listingLine}></div>

      {/* Phone Number Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        animation={false}
        dialogClassName={'loginModal'}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.addAPhoneNumber} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={s.root}>
            <div className={s.modalContent}>
              <p>
                <FormattedMessage
                  {...messages.contactHostPhonePrompt}
                  values={{ hostName: <strong>{firstName}</strong> }}
                />
              </p>
              <div className={s.noticeText}>
                <FormattedMessage {...messages.phoneNoticeText} />
              </div>

              <FormGroup className={s.space3}>
                <label className={cx(s.labelText)}>
                  <FormattedMessage {...messages.chooseACountry} />
                </label>
                <CountryList
                  input={{
                    name: 'countryCode',
                    onChange: handlePhoneChange,
                    value: countryCode
                  }}
                  className={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
                  dialCode={false}
                  getSelected={handleCountryChange}
                />
              </FormGroup>

              <FormGroup className={s.space3}>
                <label className={cx(s.labelText)}>
                  <FormattedMessage {...messages.addAPhoneNumber} />
                </label>
                <InputGroup>
                  <InputGroup.Addon>{dialCode}</InputGroup.Addon>
                  <FormControl
                    name="phoneNumber"
                    value={phoneNumber}
                    placeholder=""
                    type="text"
                    className={cx(bt.commonControlInput, phoneError && s.errorInput)}
                    onChange={handlePhoneChange}
                  />
                </InputGroup>
                {phoneError && <span className={s.errorMessage}>{getErrorMessage(phoneError)}</span>}
              </FormGroup>

              <div className={cx(bt.btnFlexContainerEnd, bt.gap16, bt.space3)}>
                <Button
                  onClick={handleCloseModal}
                  className={cx(bt.btnPrimaryBorder)}
                >
                  <FormattedMessage {...messages.cancel} />
                </Button>
                <Loader
                  type={"button"}
                  buttonType={"button"}
                  show={submitting}
                  disabled={submitting || !phoneNumber || phoneError}
                  label={props.intl.formatMessage(messages.saveContinue)}
                  handleClick={savePhoneNumber}
                  className={cx(bt.btnPrimary, 'commonLoaderFlex')}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

HostDetail.propTypes = {
  id: PropTypes.number.isRequired,
  userId: PropTypes.string.isRequired,
  personCapacity: PropTypes.number.isRequired,
  city: PropTypes.string.isRequired,
  profile: PropTypes.shape({
    profileId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    picture: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    info: PropTypes.string,
  }).isRequired,
  listingData: PropTypes.shape({
    minNight: PropTypes.number,
    maxNight: PropTypes.number,
    maxDaysNotice: PropTypes.string,
  }).isRequired,
  blockedDates: PropTypes.array,
  isHost: PropTypes.bool.isRequired,
  userBanStatus: PropTypes.number,
  country: PropTypes.string,
  urlParameters: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    guests: PropTypes.string,
    listTitle: PropTypes.string
  })
};
HostDetail.defaultProps = {
  id: 0,
  userId: null,
  userBanStatus: 0,
  personCapacity: 0,
  city: null,
  profile: {
    profileId: 0,
    displayName: null,
    picture: null,
    createdAt: null,
    info: null
  },
  listingData: {
    minNight: 0,
    maxNight: 0
  },
  blockedDates: [],
  showContactHostModal: false,
  isHost: false,
  country: null,
  urlParameters: null
}

export default injectIntl(withStyles(s, bt)(HostDetail));
